/*
 * Merchant: "There isnt a way to change sizes of items when buying an item."
 *
 * Confirmed against the real catalog: most of the Shopify Collective import
 * is ordinary sized apparel (Size option, values like XS–4XL, several real
 * variants per product) — not the one-off single-piece collectible the rest
 * of this data model was built around. snippets/sat-bootstrap.liquid's
 * product record only ever exposed `variantId`, the store's own
 * `selected_or_first_available_variant`, with no other variant ever reaching
 * the client — so however many real sizes a product actually had, the app
 * could only ever add that one, and the product detail modal
 * (snippets/sat-overlays.liquid's hasDetail block) had nothing to build a
 * size picker from even if it wanted to. sat-bootstrap.liquid now also emits
 * a `variants` array (id/title/available/stock per variant) on every product
 * record; this patch is what turns that into an actual picker and makes
 * "Add To Crate" respect it.
 *
 * Selection state mirrors assets/sat-detail-gallery-patch.js's own pattern
 * exactly (detailVariantId + detailVariantFor, not detailVariantId alone) —
 * a product-id guard so switching to a *different* product's detail (e.g.
 * "From The Same Department") can't silently keep a previous product's size
 * selected against a size list it was never chosen from.
 *
 * Committing a size: `this._products` holds one persistent record per
 * product, each with its own single `variantId` that every add-to-cart path
 * in the app (this quick "Add" button on a card, the wishlist drawer, a
 * related-product row, this very modal) reads at the moment of the click —
 * there's no separate per-line variant argument anywhere to thread a
 * selection through. Rather than duplicate addToCart's whole cart-line /
 * optimistic-update / Shopify-cart-post pipeline here, addDetail (the only
 * place a shopper actually commits a size) writes the chosen variant id onto
 * that persistent record right before calling the real addToCart, and does
 * NOT put it back afterward — every read addToCart makes of `p.variantId`
 * happens synchronously before it returns (confirmed against sat-app.js:
 * the /cart/add.js request body is built inline, not inside the later
 * .then()), so nothing async ever observes the old value, and leaving the
 * pick in place is also just correct: that's genuinely the size now sitting
 * in the real Shopify cart, so it's the right thing for a stray later
 * "Add" click elsewhere on the same product to add too.
 *
 * ---- Price now follows the selected size, this session ----
 * Merchant: "make sure that when a user changes the item version the price
 * is reflected... the leather jacket at a small is one price but is more at
 * a Large or custom." The size picker above already made stock/availability
 * track the selected size correctly, but the big price display
 * (detail.priceLabel, snippets/sat-overlays.liquid) was never touched by
 * this file — it's set once, in sat-app.js's own base `detail` builder,
 * from `p.price`, the product-level default-variant price, and nothing
 * here ever overrode it. So picking a genuinely pricier size (confirmed
 * against the real catalog: several products, e.g. an ammo pouch and a
 * duffel bag, really do price their variants differently) updated the
 * size buttons and the stock count but silently kept showing the default
 * size's price the entire time — a real, financially-relevant display bug,
 * not just cosmetic, even though the actual cart line Shopify creates on
 * Add To Crate was always correct (addToCart posts the real chosen
 * variant id straight to /cart/add.js, which prices it correctly
 * regardless of what the modal displayed).
 *
 * Fixed by requiring `price` on each entry in the `variants` array
 * (snippets/sat-product-json.liquid, same session) and overriding both
 * `detail.price` and `detail.priceLabel` here from the *chosen* variant
 * instead of leaving the base builder's product-level default in place —
 * same pattern already used just below for soldOut/stockLabel/addLabel.
 * Deliberately does NOT write the chosen price back onto the persistent
 * `this._products` record the way variantId is written back on commit:
 * the product's own base price should stay the true default price for
 * every other place that reads it (a card grid, the wishlist drawer, a
 * related-product row) — only this one open modal, for whichever size is
 * currently selected, should ever show a non-default price.
 */
(function () {
  'use strict';
  if (!window.SATComponent) { return; }

  function formatPrice(n) {
    var v = typeof n === 'number' ? n : parseFloat(n);
    if (!isFinite(v)) { return null; }
    return '$' + v.toFixed(2);
  }

  var orig = window.SATComponent.prototype.renderVals;
  window.SATComponent.prototype.renderVals = function () {
    var out = orig.call(this);
    var self = this;
    var s = this.state;

    if (!out.detail) {
      out.detailHasSizes = 'none';
      out.detailSizes = [];
      return out;
    }

    var variants = (out.detail.variants || []).filter(Boolean);
    // A product with exactly one real variant (Shopify's own "Default
    // Title" for anything with no options) has nothing to pick between —
    // leave the modal exactly as it looked before this patch.
    if (variants.length < 2) {
      out.detailHasSizes = 'none';
      out.detailSizes = [];
      return out;
    }

    var detailId = out.detail.id;
    var matchesCurrent = s.detailVariantFor === detailId &&
      variants.some(function (v) { return v.id === s.detailVariantId; });
    var selId = matchesCurrent
      ? s.detailVariantId
      : (variants.find(function (v) { return v.id === out.detail.variantId; })
        || variants.find(function (v) { return v.available; })
        || variants[0]).id;
    var chosen = variants.find(function (v) { return v.id === selId; }) || variants[0];

    out.detailHasSizes = 'flex';
    out.detailSizes = variants.map(function (v) {
      var isSel = v.id === selId;
      return {
        id: v.id,
        label: v.title,
        available: v.available,
        borderColor: isSel ? '#F0C77A' : (v.available ? 'rgba(240,199,122,.35)' : 'rgba(240,199,122,.15)'),
        bg: isSel ? '#F0C77A' : 'transparent',
        textColor: isSel ? '#2B2118' : (v.available ? '#F4EFE3' : '#5a5347'),
        cursor: v.available ? 'pointer' : 'default',
        select: v.available
          ? function () { self.setState({ detailVariantId: v.id, detailVariantFor: detailId }); }
          : function () {}
      };
    });

    // Mirror the selected size's own real availability/stock into the same
    // fields the base detail binding (sat-app.js) already computes off the
    // default variant, so the ON FILE count and the Add To Crate state track
    // whichever size is actually picked instead of always the default one.
    // Price follows the same rule now: a chosen variant with its own real
    // price (the common case — sat-product-json.liquid emits one for every
    // variant) overrides the default-variant price the base builder set;
    // a chosen variant with no usable price (defensive only — shouldn't
    // happen post-fix, but a null/NaN price falling back silently to the
    // wrong number would be worse than falling back to the original) keeps
    // whatever out.detail.price/priceLabel already were.
    var chosenPriceLabel = formatPrice(chosen.price);
    out.detail = Object.assign({}, out.detail, {
      soldOut: !chosen.available,
      stockLabel: chosen.available ? (chosen.stock > 0 ? chosen.stock + ' on file' : 'In Stock') : 'Sold Out',
      addLabel: chosen.available ? 'Add To Crate' : 'Sold Out',
      addBg: chosen.available ? '#A6402A' : '#5a5a52',
      addCursor: chosen.available ? 'pointer' : 'default',
      price: (chosenPriceLabel != null) ? chosen.price : out.detail.price,
      priceLabel: (chosenPriceLabel != null) ? chosenPriceLabel : out.detail.priceLabel
    });

    out.addDetail = function () {
      if (!chosen.available) { return; }
      var real = self._products.find(function (p) { return p.id === detailId; });
      if (!real) { return; }
      real.variantId = chosen.id;
      self.addToCart(detailId, 1);
      self.setState({ detailId: null });
    };

    return out;
  };
})();
